/**
 * Students tab of the school management screen (ADM-8): roster with filters,
 * sort, per-student analytics, invitation resend and usage reset. State and
 * actions come from useSchoolStudents.
 */
import React from 'react';
import { ActivityIndicator, TouchableOpacity, View } from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';

import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Text } from '@/components/ui/text';
import { useTranslation } from '../../../hooks/useTranslation';
import { isSchoolAdmin, isSuperAdmin, type User } from '../../../utils/roleUtils';
import { COLORS, type PendingInvitation, type School, type Student } from '../schoolManagementTypes';
import type { SchoolStudentsState } from '../useSchoolStudents';

export function StudentsTab({
  user,
  school,
  students,
  pendingInvites,
  roster,
  onInviteStudent,
  onOpenInvitations,
}: {
  user?: User;
  school: School | null;
  students: Student[];
  pendingInvites: PendingInvitation[];
  roster: SchoolStudentsState;
  onInviteStudent: () => void;
  onOpenInvitations: () => void;
}) {
  const { t } = useTranslation();
  const {
    studentFilter,
    setStudentFilter,
    studentSort,
    setStudentSort,
    loadingStudentAnalytics,
    resettingStudentId,
    resendingInvitationId,
    getFilteredAndSortedStudents,
    handleExportCSV,
    handleResendInvitation,
    handleResetUsage,
    handleOpenStudentDetail,
  } = roster;

  const filteredStudents = getFilteredAndSortedStudents();
  const sortLabels: Record<typeof studentSort, string> = {
    name: t('schoolManagement.sortOptions.name'),
    activity: t('schoolManagement.sortOptions.activity'),
    transcripts: t('schoolManagement.sortOptions.transcripts'),
    score: t('schoolManagement.sortOptions.score'),
  };

  return (
    <View className="mb-6">
      {/* Header with actions */}
      <View className="flex-row justify-between items-center mb-4">
        <Text className="text-xl font-bold text-foreground">
          {t('schoolManagement.studentsHeading', { count: filteredStudents.length })}
        </Text>
        {isSchoolAdmin(user) && (
          <View className="flex-row gap-2">
            <Button variant="default" size="sm" onPress={onInviteStudent}>
              <MaterialIcons name="add" size={16} color="white" />
              <Text className="text-white text-xs ml-1">{t('schoolManagement.invite')}</Text>
            </Button>
            {school?.id ? (
              <Button variant="outline" size="sm" onPress={onOpenInvitations}>
                <MaterialIcons name="mail" size={16} color="#1F2937" />
                <Text className="text-foreground text-xs ml-1">{t('invitations.title')}</Text>
              </Button>
            ) : null}
            <Button variant="outline" size="sm" onPress={handleExportCSV}>
              <MaterialIcons name="download" size={16} color="#1F2937" />
              <Text className="text-foreground text-xs ml-1">{t('schoolManagement.exportCSV')}</Text>
            </Button>
          </View>
        )}
      </View>

      {/* Filters and Sort — show when ANY students exist, including pending invites */}
      {(students.length > 0 || pendingInvites.length > 0) && (
        <View className="mb-4">
          {/* Filter Buttons */}
          <View className="flex-row gap-2 mb-3 flex-wrap">
            <TouchableOpacity
              className={`px-4 py-2 rounded-lg ${studentFilter === 'all' ? 'bg-primary' : 'bg-gray-200'
                }`}
              onPress={() => setStudentFilter('all')}
            >
              <Text
                className={`text-sm font-medium ${studentFilter === 'all' ? 'text-white' : 'text-gray-700'
                  }`}
              >
                {t('schoolManagement.filters.all')}
              </Text>
            </TouchableOpacity>
            <TouchableOpacity
              className={`px-4 py-2 rounded-lg ${studentFilter === 'active' ? 'bg-primary' : 'bg-gray-200'
                }`}
              onPress={() => setStudentFilter('active')}
            >
              <Text
                className={`text-sm font-medium ${studentFilter === 'active' ? 'text-white' : 'text-gray-700'
                  }`}
              >
                {t('schoolManagement.filters.active')}
              </Text>
            </TouchableOpacity>
            <TouchableOpacity
              className={`px-4 py-2 rounded-lg ${studentFilter === 'inactive' ? 'bg-primary' : 'bg-gray-200'
                }`}
              onPress={() => setStudentFilter('inactive')}
            >
              <Text
                className={`text-sm font-medium ${studentFilter === 'inactive' ? 'text-white' : 'text-gray-700'
                  }`}
              >
                {t('schoolManagement.filters.inactive')}
              </Text>
            </TouchableOpacity>
            <TouchableOpacity
              className={`px-4 py-2 rounded-lg ${studentFilter === 'invited' ? 'bg-primary' : 'bg-gray-200'
                }`}
              onPress={() => setStudentFilter('invited')}
            >
              <Text
                className={`text-sm font-medium ${studentFilter === 'invited' ? 'text-white' : 'text-gray-700'
                  }`}
              >
                {t('invitations.invited')}
              </Text>
            </TouchableOpacity>
          </View>

          {/* Sort Dropdown */}
          <View className="flex-row items-center gap-2">
            <Text className="text-sm text-muted-foreground">
              {t('schoolManagement.sortBy')}
            </Text>
            <View className="flex-row gap-2">
              {(['name', 'activity', 'transcripts', 'score'] as const).map((sortOption) => (
                <TouchableOpacity
                  key={sortOption}
                  className={`px-3 py-1 rounded ${studentSort === sortOption ? 'bg-blue-100' : 'bg-gray-100'
                    }`}
                  onPress={() => setStudentSort(sortOption)}
                >
                  <Text
                    className={`text-xs ${studentSort === sortOption ? 'text-blue-700 font-semibold' : 'text-gray-600'
                      }`}
                  >
                    {sortLabels[sortOption]}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>
          </View>
        </View>
      )}

      {/* Loading Analytics State */}
      {loadingStudentAnalytics && (
        <Card className="mb-4 bg-blue-50">
          <CardContent className="p-4 flex-row items-center">
            <ActivityIndicator size="small" color={COLORS.primary} />
            <Text className="text-sm text-muted-foreground ml-3">
              {t('schoolManagement.loadingStudentAnalytics')}
            </Text>
          </CardContent>
        </Card>
      )}

      {/* Student List */}
      {filteredStudents.length > 0 ? (
        <View className="gap-3">
          {filteredStudents.map((student) => (
            <TouchableOpacity
              key={student.id}
              onPress={() => !student.is_invited && handleOpenStudentDetail(student.id)}
              activeOpacity={student.is_invited ? 1 : 0.7}
              disabled={student.is_invited}
            >
              <Card>
                <CardContent className="p-4">
                  <View className="flex-row items-start justify-between mb-3">
                    <View className="flex-1">
                      <Text className="text-base font-semibold text-foreground mb-1">
                        {student.full_name || student.email}
                      </Text>
                      <Text className="text-sm text-muted-foreground mb-1">{student.email}</Text>
                    </View>
                    <View
                      className={`px-3 py-1 rounded-full ${student.is_invited
                          ? 'bg-blue-100'
                          : student.is_active
                            ? 'bg-green-100'
                            : 'bg-red-100'
                        }`}
                    >
                      <Text
                        className={`text-xs font-semibold ${student.is_invited
                            ? 'text-blue-700'
                            : student.is_active
                              ? 'text-green-700'
                              : 'text-red-700'
                          }`}
                      >
                        {student.is_invited
                          ? t('invitations.invited')
                          : student.is_active
                            ? t('schoolManagement.student.statusActive')
                            : t('schoolManagement.student.statusInactive')}
                      </Text>
                    </View>
                  </View>

                  {/* For Invited Students - Show Invitation Info */}
                  {student.is_invited ? (
                    <View>
                      <View className="flex-row items-center mb-2">
                        <MaterialIcons name="mail-outline" size={14} color="#6b7280" />
                        <Text className="text-xs text-muted-foreground ml-1">
                          {t('invitations.invitedStatus')}
                        </Text>
                      </View>
                      {student.invitation_expires && (
                        <View className="flex-row items-center mb-2">
                          <MaterialIcons name="schedule" size={14} color="#6b7280" />
                          <Text className="text-xs text-muted-foreground ml-1">
                            {t('invitations.expires', { date: new Date(student.invitation_expires).toLocaleDateString() })}
                          </Text>
                        </View>
                      )}
                      <View className="border-t border-gray-200 pt-2 mt-2">
                        <Text className="text-xs text-muted-foreground">
                          {t('schoolManagement.student.invitedAt', {
                            date: new Date(student.created_at).toLocaleDateString(),
                          })}
                        </Text>
                      </View>
                      {/* Re-send Invitation Button */}
                      {isSchoolAdmin(user) && (
                        <View className="flex-row justify-end mt-3">
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={resendingInvitationId === student.invitation_id}
                            onPress={() => student.invitation_id && handleResendInvitation(student.invitation_id)}
                          >
                            {resendingInvitationId === student.invitation_id ? (
                              <>
                                <ActivityIndicator size="small" color="#1F2937" />
                                <Text className="text-xs text-foreground ml-2">
                                  {t('schoolManagement.student.sending')}
                                </Text>
                              </>
                            ) : (
                              <>
                                <MaterialIcons name="send" size={16} color="#1F2937" />
                                <Text className="text-xs text-foreground ml-1">{t('invitations.actions.resendInvitation')}</Text>
                              </>
                            )}
                          </Button>
                        </View>
                      )}
                    </View>
                  ) : (
                    <>
                      {/* Analytics Data for Regular Students */}
                      <View className="flex-row flex-wrap gap-3 mb-2">
                        <View className="flex-row items-center">
                          <MaterialIcons name="access-time" size={14} color="#6b7280" />
                          <Text className="text-xs text-muted-foreground ml-1">
                            {t('schoolManagement.student.lastActivityLabel')}{' '}
                            {student.last_activity
                              ? new Date(student.last_activity).toLocaleDateString()
                              : t('schoolManagement.student.never')}
                          </Text>
                        </View>
                        <View className="flex-row items-center">
                          <MaterialIcons name="description" size={14} color="#6b7280" />
                          <Text className="text-xs text-muted-foreground ml-1">
                            {t('schoolManagement.student.transcriptsCount', {
                              completed: student.transcripts_this_month || 0,
                              total: student.monthly_transcripts_limit || 0,
                            })}
                          </Text>
                        </View>
                        <View className="flex-row items-center">
                          <MaterialIcons name="chat" size={14} color="#6b7280" />
                          <Text className="text-xs text-muted-foreground ml-1">
                            {t('schoolManagement.student.messagesCount', {
                              count: student.chat_messages_total || 0,
                            })}
                          </Text>
                        </View>
                        {student.avg_icf_score !== null && student.avg_icf_score !== undefined && (
                          <View className="flex-row items-center">
                            <MaterialIcons name="star" size={14} color="#F59E0B" />
                            <Text className="text-xs text-muted-foreground ml-1">
                              {t('schoolManagement.student.avgScore', {
                                score: student.avg_icf_score.toFixed(1),
                              })}
                            </Text>
                          </View>
                        )}
                      </View>

                      {/* Original Info */}
                      <View className="border-t border-gray-200 pt-2 mt-2">
                        <Text className="text-xs text-muted-foreground">
                          {t('schoolManagement.student.enrolledAt', {
                            date: new Date(student.created_at).toLocaleDateString(),
                          })}
                        </Text>
                      </View>
                    </>
                  )}

                  {/* Super Admin Actions */}
                  {user && isSuperAdmin(user as User) && (
                    <View className="flex-row justify-end mt-3">
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={resettingStudentId === student.id}
                        onPress={() => handleResetUsage(student)}
                      >
                        {resettingStudentId === student.id ? (
                          <>
                            <ActivityIndicator size="small" color="#1F2937" />
                            <Text className="text-xs text-foreground ml-2">Resetting...</Text>
                          </>
                        ) : (
                          <>
                            <MaterialIcons name="refresh" size={16} color="#1F2937" />
                            <Text className="text-xs text-foreground ml-1">Reset Usage</Text>
                          </>
                        )}
                      </Button>
                    </View>
                  )}
                </CardContent>
              </Card>
            </TouchableOpacity>
          ))}
        </View>
      ) : (students.length === 0 && pendingInvites.length === 0) ? (
        <Card>
          <CardContent className="p-8 items-center">
            <MaterialIcons name="group" size={48} color="#6b7280" />
            <Text className="text-lg font-medium text-foreground mt-4 mb-2">
              {t('schoolManagement.noStudents')}
            </Text>
            <Text className="text-base text-muted-foreground text-center mb-6">
              {t('schoolManagement.inviteFirstStudent')}
            </Text>
            {isSchoolAdmin(user) && (
              <Button variant="default" onPress={onInviteStudent}>
                <Text className="text-white font-medium">
                  {t('schoolManagement.quickActionsButtons.inviteStudent')}
                </Text>
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-8 items-center">
            <MaterialIcons name="filter-list" size={48} color="#6b7280" />
            <Text className="text-lg font-medium text-foreground mt-4 mb-2">
              {t('schoolManagement.noStudentsFiltered')}
            </Text>
            <Text className="text-base text-muted-foreground text-center">
              {t('schoolManagement.noStudentsFilteredHint')}
            </Text>
          </CardContent>
        </Card>
      )}
    </View>
  );
}
